import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../auth/AuthProvider'
import { RulesetEditor } from '../components/RulesetEditor'
import type { Ruleset } from '../lib/ruleset'
import { MAJOR_LABEL } from './AdminTournaments'
import { describeLocal, fromLocalInput, localZoneLabel, toLocalInput } from '../lib/datetime'

interface LookupEvent {
  event_id: number
  event_name: string
  course_name: string
  location: string
  tour: string
  date_start: string
  suggested_cut_position: number
}

interface Form {
  name: string
  major: string
  counts_toward_marathon: string
  course: string
  par: number
  starts_at: string
  lock_time: string
  start_delay_minutes: number
  status: string
  cut_position: number
  dg_tour: string
  dg_event_id: string
  stryktips_enabled: boolean
  stryktips_tiebreak_label: string
  ruleset: Ruleset
}

const STATUSES = [
  { value: 'draft', label: 'Utkast — osynlig för deltagare' },
  { value: 'open', label: 'Anmälan öppen' },
  { value: 'locked', label: 'Låst — laginlämning stängd' },
  { value: 'live', label: 'Pågår' },
  // Etiketten stod tidigare "Avslutad — räknas i maratontabellen", vilket
  // blandade ihop två oberoende saker: statusen avslutar tävlingen,
  // counts_toward_marathon avgör om den räknas. Den som avslutade ett genrep
  // fick rimligen för sig att det var på väg in i säsongen.
  { value: 'completed', label: 'Avslutad — resultatet är slutgiltigt' },
]


export function AdminTournament() {
  const { id = '' } = useParams()
  const { profile } = useAuth()
  const navigate = useNavigate()

  const [form, setForm] = useState<Form | null>(null)
  const [major, setMajor] = useState('')
  const [season, setSeason] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [busy, setBusy] = useState(false)
  const [fieldCount, setFieldCount] = useState<number | null>(null)
  const [looking, setLooking] = useState(false)
  const [lookupNote, setLookupNote] = useState<string | null>(null)
  const [teamCount, setTeamCount] = useState<number | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [candidates, setCandidates] = useState<LookupEvent[]>([])
  const [fieldComplete, setFieldComplete] = useState<boolean | null>(null)

  useEffect(() => {
    async function load() {
      const { data, error } = await supabase
        .from('tournaments')
        .select('*')
        .eq('id', id)
        .maybeSingle()

      if (error) { setError(error.message); return }
      if (!data) { setError('Turneringen finns inte.'); return }

      setMajor((data.major as string) ?? '')
      setSeason(data.season as number)
      setForm({
        name: data.name ?? '',
        major: (data.major as string) ?? '',
        counts_toward_marathon:
          data.counts_toward_marathon === null ? 'auto' : String(data.counts_toward_marathon),
        course: data.course ?? '',
        par: data.par ?? 72,
        starts_at: toLocalInput(data.starts_at),
        lock_time: toLocalInput(data.lock_time),
        start_delay_minutes: data.start_delay_minutes ?? 0,
        status: data.status,
        cut_position: data.cut_position ?? 60,
        dg_tour: data.dg_tour ?? 'pga',
        dg_event_id: data.dg_event_id ?? '',
        stryktips_enabled: data.stryktips_enabled ?? true,
        stryktips_tiebreak_label: data.stryktips_tiebreak_label ?? '',
        ruleset: (data.ruleset ?? {}) as Ruleset,
      })

      const { count } = await supabase
        .from('tournament_field')
        .select('*', { count: 'exact', head: true })
        .eq('tournament_id', id)
      setFieldCount(count ?? 0)

      const { count: teams } = await supabase
        .from('teams')
        .select('*', { count: 'exact', head: true })
        .eq('tournament_id', id)
      setTeamCount(teams ?? 0)
      setFieldComplete(
        (count ?? 0) === 0 ? null : Boolean(data.field_complete_at),
      )
    }

    void load()
  }, [id])

  if (profile && profile.role !== 'admin') {
    return (
      <div className="page">
        <div className="notice notice--error">Den här sidan är bara för admin.</div>
      </div>
    )
  }

  if (error && !form) return <div className="page"><div className="notice notice--error">{error}</div></div>
  if (!form) return <div className="page"><p className="muted">Hämtar…</p></div>

  const patch = (p: Partial<Form>) => { setForm({ ...form, ...p }); setSaved(false) }

  /**
   * Uppslaget går via workern, inte direkt mot DataGolf: nyckeln får inte
   * ligga i en frontend-bundle. Workern verifierar sessionen och kräver admin.
   */
  async function lookup() {
    if (!form) return
    setLooking(true)
    setError(null)
    setLookupNote(null)

    const base = import.meta.env.VITE_DATAGOLF_WORKER_URL
    if (!base) {
      setError('VITE_DATAGOLF_WORKER_URL är inte satt i bygget.')
      setLooking(false)
      return
    }

    const { data: sess } = await supabase.auth.getSession()
    const res = await fetch(
      `${base}/event-lookup?tour=${encodeURIComponent(form.dg_tour)}&q=${encodeURIComponent(form.name)}`,
      { headers: { Authorization: `Bearer ${sess.session?.access_token ?? ''}` } },
    )

    setLooking(false)

    if (!res.ok) {
      setError(
        res.status === 404
          ? 'Uppslaget nekade — kontrollera att du är admin och att workern är deployad.'
          : `Uppslaget misslyckades (${res.status}).`,
      )
      return
    }

    const result = await res.json()

    if (result.match) {
      applyEvent(result.match)
      setCandidates([])
    } else {
      // Otydlig sökning: visa listan i stället för att gissa åt användaren.
      setCandidates(result.candidates ?? [])
      setLookupNote(
        result.candidates?.length
          ? `Ingen entydig träff på "${form.name}". Välj nedan.`
          : `Inget kommande event matchar "${form.name}" på ${form.dg_tour}-touren.`,
      )
    }
  }

  function applyEvent(ev: LookupEvent) {
    // Startdatum är torsdag; DataGolf ger inget klockslag för första boll,
    // så 13:00 lokalt är en utgångspunkt att justera.
    const start = new Date(`${ev.date_start}T13:00:00`)
    patch({
      name: ev.event_name,
      course: ev.course_name,
      dg_event_id: String(ev.event_id),
      dg_tour: ev.tour,
      cut_position: ev.suggested_cut_position,
      starts_at: toLocalInput(start.toISOString()),
      lock_time: toLocalInput(new Date(start.getTime() - 60 * 60 * 1000).toISOString()),
    })
    setCandidates([])
    setLookupNote(
      `${ev.event_name} — ${ev.course_name}, ${ev.location}, start ${ev.date_start}. ` +
        'Par och tider får sättas för hand.',
    )
  }

  /** Skriver statusen direkt, utan att kräva ett varv via spara-knappen. */
  async function setStatus(status: string) {
    setBusy(true)
    setError(null)

    const { error } = await supabase.from('tournaments').update({ status }).eq('id', id)

    setBusy(false)
    if (error) setError(error.message)
    else { patch({ status }); setSaved(true) }
  }

  async function remove() {
    setBusy(true)
    setError(null)

    const { error } = await supabase.from('tournaments').delete().eq('id', id)

    setBusy(false)
    if (error) setError(error.message)
    else navigate('/admin')
  }

  async function onSave(e: FormEvent) {
    e.preventDefault()
    if (!form) return
    setBusy(true)
    setError(null)

    const { error } = await supabase
      .from('tournaments')
      .update({
        name: form.name.trim(),
        major: form.major || null,
        counts_toward_marathon:
          form.counts_toward_marathon === 'auto' ? null : form.counts_toward_marathon === 'true',
        course: form.course.trim() || null,
        par: form.par,
        starts_at: fromLocalInput(form.starts_at),
        lock_time: fromLocalInput(form.lock_time),
        start_delay_minutes: form.start_delay_minutes,
        status: form.status,
        cut_position: form.cut_position,
        dg_tour: form.dg_tour.trim(),
        dg_event_id: form.dg_event_id.trim() || null,
        stryktips_enabled: form.stryktips_enabled,
        stryktips_tiebreak_label: form.stryktips_tiebreak_label.trim() || null,
        ruleset: form.ruleset,
      })
      .eq('id', id)

    setBusy(false)
    if (error) setError(error.message)
    else setSaved(true)
  }

  const lockAfterStart =
    form.lock_time && form.starts_at && new Date(form.lock_time) > new Date(form.starts_at)

  return (
    <form className="page stack" onSubmit={onSave}>
      <div>
        <p className="eyebrow">
          Admin · {major ? `${MAJOR_LABEL[major] ?? major} ${season}` : `Tävling ${season}`}
        </p>
        <h1>{form.name || 'Turnering'}</h1>
      </div>

      {error && <div className="notice notice--error">{error}</div>}
      {saved && <div className="notice notice--ok">Sparat.</div>}

      <section className="card stack">
        <h2>Tävlingstyp</h2>

        <div>
          <label htmlFor="major">Major</label>
          <select id="major" value={form.major} onChange={(e) => patch({ major: e.target.value })}>
            <option value="">Ingen — vanlig tävling eller genrep</option>
            <option value="masters">Masters</option>
            <option value="pga">PGA Championship</option>
            <option value="us_open">US Open</option>
            <option value="the_open">The Open</option>
          </select>
        </div>

        <div>
          <label htmlFor="counts">Räknas i maratontabellen</label>
          <select
            id="counts"
            value={form.counts_toward_marathon}
            onChange={(e) => patch({ counts_toward_marathon: e.target.value })}
          >
            <option value="auto">
              Standard — {form.major ? 'ja, det är en major' : 'nej, det är ingen major'}
            </option>
            <option value="true">Ja, räkna med den</option>
            <option value="false">Nej, håll den utanför</option>
          </select>
          <p className="muted">
            Genrep och vanliga PGA-veckor hålls utanför automatiskt. Vill ni ha med
            The Players i säsongen sätter ni det här — och en inställd major kan
            uteslutas på samma sätt.
          </p>
        </div>
      </section>

      <section className="card stack">
        <h2>Grunduppgifter</h2>

        <div>
          <label htmlFor="name">Namn</label>
          <input id="name" type="text" required value={form.name}
                 onChange={(e) => patch({ name: e.target.value })} />
        </div>

        <div>
          <label htmlFor="course">Bana</label>
          <input id="course" type="text" value={form.course}
                 onChange={(e) => patch({ course: e.target.value })}
                 placeholder="Augusta National" />
        </div>

        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ maxWidth: 110 }}>
            <label htmlFor="par">Par</label>
            <input id="par" type="number" min={60} max={80} value={form.par}
                   onChange={(e) => patch({ par: Number(e.target.value) })} />
          </div>
          <div style={{ maxWidth: 150 }}>
            <label htmlFor="cutpos">Cut-position</label>
            <input id="cutpos" type="number" min={1} max={160} value={form.cut_position}
                   onChange={(e) => patch({ cut_position: Number(e.target.value) })} />
          </div>
        </div>
        <p className="muted">
          Cut-positionen används för att projicera cut-linjen innan cutten fallit.
          Masters 50, US Open 60, PGA och The Open 70.
        </p>
      </section>

      <section className="card stack">
        <h2>Tider</h2>
        <p className="muted">
          Klockslagen visas i din egen tidszon ({localZoneLabel()}). De lagras som
          absoluta tidpunkter, så alla ser samma ögonblick oavsett var de är.
        </p>

        {/* Väljarens utseende — am/pm, söndag först — styrs av WEBBLÄSARENS
            språkinställning, inte av sidans lang och inte av lang på fältet.
            Det går alltså inte att rätta härifrån.

            I stället ekas det valda värdet tillbaka på svenska. Det tar bort
            den faktiska risken: att tro att man satt 13:00 när man satt 01:00.
            Fel lock_time har redan kostat en felsökning. */}
        <div>
          <label htmlFor="lock">Laginlämning stänger</label>
          <input id="lock" type="datetime-local" lang="sv-SE" required value={form.lock_time}
                 onChange={(e) => patch({ lock_time: e.target.value })} />
          {form.lock_time && (
            <p className="muted">{describeLocal(form.lock_time)}</p>
          )}
        </div>

        <div>
          <label htmlFor="start">Första tee</label>
          <input id="start" type="datetime-local" lang="sv-SE" required value={form.starts_at}
                 onChange={(e) => patch({ starts_at: e.target.value })} />
          {form.starts_at && (
            <p className="muted">{describeLocal(form.starts_at)}</p>
          )}
        </div>

        {lockAfterStart && (
          <div className="notice notice--error">
            Inlämningen kan inte stänga efter första tee. Databasen avvisar det.
          </div>
        )}

        <div style={{ maxWidth: 170 }}>
          <label htmlFor="delay">Fördröjning innan lag visas (min)</label>
          <input id="delay" type="number" min={0} max={720} value={form.start_delay_minutes}
                 onChange={(e) => patch({ start_delay_minutes: Number(e.target.value) })} />
        </div>
        <p className="muted">
          Andras laguppställningar visas först vid första tee plus den här fördröjningen
          — inte när inlämningen stänger. Det var en konkret miss i v1.
        </p>
      </section>

      <section className="card stack">
        <h2>Status</h2>

        {fieldComplete !== null && (
          <div className={fieldComplete ? 'notice notice--ok' : 'notice'} style={{ borderColor: 'var(--border)' }}>
            {fieldComplete
              ? 'Startfältet är spikat — samtliga spelare har tee-tid för rond 1.'
              : 'Startfältet rör sig fortfarande. Alla spelare har inte tee-tid för rond 1 än.'}
          </div>
        )}

        {/* Snabbknappar: det vanliga flödet är att öppna, låsa och starta i
            tur och ordning, och det ska inte kräva att man hittar rätt rad i
            en lista och sedan trycker spara. */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {form.status === 'draft' && (
            <button type="button" className="primary" onClick={() => void setStatus('open')} disabled={busy}>
              Öppna anmälan
            </button>
          )}
          {form.status === 'open' && (
            <button type="button" onClick={() => void setStatus('locked')} disabled={busy}>
              Lås laginlämningen
            </button>
          )}
          {(form.status === 'open' || form.status === 'locked') && (
            <button type="button" className="primary" onClick={() => void setStatus('live')} disabled={busy}>
              Starta tävlingen
            </button>
          )}
          {form.status === 'live' && (
            <button type="button" onClick={() => void setStatus('completed')} disabled={busy}>
              Avsluta tävlingen
            </button>
          )}
        </div>

        <label htmlFor="status-select">Eller sätt direkt</label>
        <select id="status-select" value={form.status} onChange={(e) => patch({ status: e.target.value })}>
          {STATUSES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <p className="muted">
          Statusen flyttas fram automatiskt av live-scoring-workern:{' '}
          <code>open</code> blir <code>locked</code> när inlämningen stänger, och{' '}
          <code>locked</code> blir <code>live</code> vid första tee. Knapparna
          behövs bara för att gå i förväg eller rätta något.
        </p>
        <p className="muted">
          Workern plockar upp turneringar med status <code>locked</code> eller{' '}
          <code>live</code> — en i taget, den med senaste starttid. Lämnas en gammal
          tävling kvar på <code>live</code> skrivs den pågående veckans scorer på den.
        </p>
        <p className="muted">
          <code>completed</code> avslutar tävlingen. Om den <em>räknas</em> i
          maratontabellen avgörs av Räknas i maratontabellen ovan — just nu{' '}
          <strong>
            {form.counts_toward_marathon === 'auto'
              ? form.major
                ? 'ja, det är en major'
                : 'nej, den hålls utanför'
              : form.counts_toward_marathon === 'true'
                ? 'ja, den är med'
                : 'nej, den är utesluten'}
          </strong>
          .
        </p>
      </section>

      <section className="card stack">
        <h2>DataGolf</h2>

        <div className="stack">
          <div>
            <button type="button" onClick={() => void lookup()} disabled={looking}>
              {looking ? 'Söker…' : 'Slå upp i DataGolfs spelschema'}
            </button>
            <p className="muted">
              Söker på namnet du skrivit ovan bland kommande tävlingar på{' '}
              {form.dg_tour}-touren och fyller i bana, ort, event-id och startdatum.
              Lämna namnet tomt för att se hela listan.{' '}
              <strong>Par går inte att hämta</strong> — DataGolf levererar det inte,
              varken i schemat eller i fältdatan.
            </p>
          </div>

          {lookupNote && <div className="notice notice--ok">{lookupNote}</div>}

          {candidates.length > 0 && (
            <div className="board">
              {candidates.map((c) => (
                <button
                  key={c.event_id}
                  type="button"
                  className="field-row"
                  onClick={() => applyEvent(c)}
                >
                  <span className="field-row__rank">{c.date_start}</span>
                  <span className="field-row__name">{c.event_name}</span>
                  <span className="field-row__meta">{c.course_name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ maxWidth: 130 }}>
            <label htmlFor="tour">Tour</label>
            <input id="tour" type="text" value={form.dg_tour}
                   onChange={(e) => patch({ dg_tour: e.target.value })} />
          </div>
          <div style={{ maxWidth: 170 }}>
            <label htmlFor="event">Event-id</label>
            <input id="event" type="text" value={form.dg_event_id}
                   onChange={(e) => patch({ dg_event_id: e.target.value })}
                   placeholder="524" />
          </div>
        </div>
        <p className="muted">
          Fältsynken matchar på event-id. Utan det svarar den <em>skipped</em> och
          skriver inga spelare. Id:t syns i <code>field-updates</code> under
          tävlingsveckan.
          {fieldCount !== null && ` Fältet innehåller nu ${fieldCount} spelare.`}
        </p>
      </section>

      <section className="card stack">
        <h2>Regeluppsättning</h2>
        <RulesetEditor value={form.ruleset} onChange={(ruleset) => patch({ ruleset })} />
      </section>

      <section className="card stack">
        <h2>Stryktips</h2>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, textTransform: 'none', letterSpacing: 'normal', fontSize: '0.95rem', color: 'var(--white)' }}>
          <input type="checkbox" checked={form.stryktips_enabled} style={{ width: 'auto' }}
                 onChange={(e) => patch({ stryktips_enabled: e.target.checked })} />
          Stryktips på den här majoren
        </label>
        <div>
          <label htmlFor="tiebreak">Utslagsfrågan</label>
          <input id="tiebreak" type="text" value={form.stryktips_tiebreak_label}
                 onChange={(e) => patch({ stryktips_tiebreak_label: e.target.value })}
                 placeholder="5 japaners totala score mot par" />
        </div>
      </section>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button className="primary" type="submit" disabled={busy || Boolean(lockAfterStart)}>
          {busy ? 'Sparar…' : 'Spara'}
        </button>
        <button type="button" onClick={() => navigate('/admin')}>Tillbaka</button>
      </div>

      <section className="card stack">
        <h2>Ta bort turneringen</h2>
        <p className="muted">
          Tar med sig fältet, alla lag, live-scorer och stryktips via kaskad.
          {teamCount !== null && teamCount > 0 && (
            <> <strong>{teamCount} inlämnade lag försvinner.</strong></>
          )}
          {' '}Går inte att ångra.
        </p>

        {!confirmDelete ? (
          <div>
            <button type="button" onClick={() => setConfirmDelete(true)}>
              Ta bort
            </button>
          </div>
        ) : (
          <div className="stack">
            <div className="notice notice--error">
              Skriv turneringens namn för att bekräfta: <strong>{form.name}</strong>
            </div>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={form.name}
              autoComplete="off"
            />
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => void remove()}
                disabled={busy || deleteConfirmText.trim() !== form.name.trim()}
              >
                {busy ? 'Tar bort…' : 'Ja, ta bort'}
              </button>
              <button type="button" onClick={() => { setConfirmDelete(false); setDeleteConfirmText('') }}>
                Avbryt
              </button>
            </div>
          </div>
        )}
      </section>
    </form>
  )
}
